import { CheckCircle2, Pencil, X as XIcon } from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { useEffect, useMemo, useState } from "react";
import ModifyBookingModal from "../../components/ModifyBookingModal";
import ExternalCompleteModal from "../../components/ExternalCompleteModal";
import { ddmmyyyy, time12h } from "../../lib/dates";

const STATUS_TABS = [
    { key: "all", label: "All" },
    { key: "confirmed", label: "Confirmed" },
    { key: "cancelled", label: "Cancelled" },
];

const PAY_TABS = [
    { key: "all", label: "All Payments" },
    { key: "paid", label: "Paid in Full" },
    { key: "partial", label: "Partial (Pending Balance)" },
    { key: "pending", label: "Pending" },
];

export default function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [payFilter, setPayFilter] = useState("all");
    const [modifyTarget, setModifyTarget] = useState(null);
    const [externalCompleteId, setExternalCompleteId] = useState(null);  // Iter 35
    // Iter 39 — Clear Entry (bulk delete)
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);

    const fetchBookings = async () => {
        const { data } = await api.get("/bookings");
        setBookings(data);
    };

    useEffect(() => { fetchBookings(); }, []);

    const collectBalance = async (id) => {
        if (!confirm("Mark balance as collected at venue?")) return;
        try {
            await api.post(`/bookings/${id}/collect-balance`, { method: "cash" });
            toast.success("Balance collected. Booking is now fully paid.");
            fetchBookings();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const cancelByAdmin = async (id) => {
        if (!confirm("Cancel this booking with FULL refund (admin override)?")) return;
        try {
            const { data } = await api.post(`/bookings/${id}/cancel`);
            toast.success(`Cancelled. Refund ₹${data.refund || 0}`);
            fetchBookings();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cancel failed");
        }
    };

    const filtered = useMemo(() => {
        return bookings
            .filter((b) => statusFilter === "all" ? true : b.status === statusFilter)
            .filter((b) => payFilter === "all" ? true : b.payment_status === payFilter);
    }, [bookings, statusFilter, payFilter]);

    // Iter 38 — pagination: 20 per page, S.No. resets to 1 whenever the filtered set changes.
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [statusFilter, payFilter]);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // Iter 39 — Bulk delete helpers
    const toggleSelect = (id) => setSelectedIds(s => {
        const nxt = new Set(s);
        nxt.has(id) ? nxt.delete(id) : nxt.add(id);
        return nxt;
    });
    const togglePageAll = () => setSelectedIds(s => {
        const nxt = new Set(s);
        const allChecked = pageRows.every(r => nxt.has(r.id));
        pageRows.forEach(r => allChecked ? nxt.delete(r.id) : nxt.add(r.id));
        return nxt;
    });
    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedIds.size} booking${selectedIds.size > 1 ? "s" : ""}? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const { data } = await api.post("/admin/bookings/bulk-delete", { booking_ids: [...selectedIds] });
            toast.success(`Deleted ${data.deleted_count} booking${data.deleted_count === 1 ? "" : "s"}`);
            setSelectedIds(new Set());
            fetchBookings();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk delete failed");
        } finally {
            setBulkDeleting(false);
        }
    };

    const counts = useMemo(() => {
        const total = bookings.length;
        const paid = bookings.filter((b) => b.payment_status === "paid").length;
        const partial = bookings.filter((b) => b.payment_status === "partial").length;
        const pending = bookings.filter((b) => b.payment_status === "pending").length;
        const balanceDue = bookings
            .filter((b) => b.status !== "cancelled")
            .reduce((s, b) => s + (b.balance_due || 0), 0);
        const collectedRevenue = bookings
            .filter((b) => b.status !== "cancelled")
            .reduce((s, b) => s + (b.paid_amount || 0), 0);
        return { total, paid, partial, pending, balanceDue, collectedRevenue };
    }, [bookings]);

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <h1 className="font-display text-3xl font-bold uppercase text-white">Bookings</h1>
                <p className="text-slate-400 text-sm mt-1">All bookings with payment tracking across centres.</p>

                {/* Payment KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
                    <PayKpi label="Total" value={counts.total} testid="pay-kpi-total" />
                    <PayKpi label="Paid in Full" value={counts.paid} accent="green" testid="pay-kpi-paid" />
                    <PayKpi label="Partial" value={counts.partial} accent="amber" testid="pay-kpi-partial" />
                    <PayKpi label="Collected" value={`₹${counts.collectedRevenue}`} accent="green" testid="pay-kpi-collected" />
                    <PayKpi label="Balance Due (Venue)" value={`₹${counts.balanceDue}`} accent="amber" testid="pay-kpi-balance" />
                </div>

                {/* Status filter */}
                <div className="mt-6">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500 mb-2">Booking Status</div>
                    <div className="flex gap-2 flex-wrap">
                        {STATUS_TABS.map((s) => (
                            <button key={s.key} onClick={() => setStatusFilter(s.key)}
                                data-testid={`status-filter-${s.key}`}
                                className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                    statusFilter === s.key ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                                }`}>{s.label}</button>
                        ))}
                    </div>
                </div>

                {/* Payment filter */}
                <div className="mt-4">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-500 mb-2">Payment Status</div>
                    <div className="flex gap-2 flex-wrap">
                        {PAY_TABS.map((s) => (
                            <button key={s.key} onClick={() => setPayFilter(s.key)}
                                data-testid={`pay-filter-${s.key}`}
                                className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                                    payFilter === s.key ? "bg-[#00FF66]/20 text-[#00FF66] border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                                }`}>{s.label}</button>
                        ))}
                    </div>
                </div>

                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                    {/* Iter 39 — Clear Entry toolbar (shown only when at least 1 row selected) */}
                    {selectedIds.size > 0 && (
                        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-950/40 border-b border-red-900" data-testid="bulk-toolbar">
                            <span className="text-xs uppercase tracking-widest text-red-300 font-bold">
                                {selectedIds.size} selected
                            </span>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-selection"
                                    className="px-3 h-9 text-[10px] font-bold uppercase tracking-widest rounded border border-slate-700 text-slate-300 hover:bg-slate-800">
                                    Clear Selection
                                </button>
                                <button onClick={bulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-btn"
                                    className="px-4 h-9 text-[10px] font-bold uppercase tracking-widest rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
                                    {bulkDeleting ? "Deleting…" : `Clear Entry (${selectedIds.size})`}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800 bg-slate-950/50">
                                    <th className="text-center py-3 px-3 w-10">
                                        <input type="checkbox" data-testid="bulk-select-all"
                                            checked={pageRows.length > 0 && pageRows.every(r => selectedIds.has(r.id))}
                                            onChange={togglePageAll}
                                            className="w-4 h-4 accent-red-500" />
                                    </th>
                                    <th className="text-left py-3 px-4 w-16">S.No.</th>
                                    <th className="text-left py-3 px-4">Code</th>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Centre · Turf</th>
                                    <th className="text-left py-3 px-4">Date · Time</th>
                                    <th className="text-left py-3 px-4">Source</th>
                                    <th className="text-right py-3 px-4">Total</th>
                                    <th className="text-right py-3 px-4">Paid</th>
                                    <th className="text-right py-3 px-4">Balance</th>
                                    <th className="text-left py-3 px-4">Payment</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-right py-3 px-4">Action</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-300">
                                {filtered.length === 0 && (
                                    <tr><td colSpan={13} className="py-8 text-center text-slate-500">No bookings match the filters.</td></tr>
                                )}
                                {pageRows.map((b, idx) => (
                                    <tr key={b.id} className={`border-b border-slate-900 ${selectedIds.has(b.id) ? "bg-red-950/20" : ""}`} data-testid={`admin-booking-${b.id}`}>
                                        <td className="py-3 px-3 text-center">
                                            <input type="checkbox" data-testid={`bulk-select-${b.id}`}
                                                checked={selectedIds.has(b.id)}
                                                onChange={() => toggleSelect(b.id)}
                                                className="w-4 h-4 accent-red-500" />
                                        </td>
                                        <td className="py-3 px-4 text-slate-500 font-mono text-xs" data-testid={`admin-booking-sno-${b.id}`}>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                        <td className="py-3 px-4 font-mono text-xs text-[#00FF66]">{b.booking_code}</td>
                                        <td className="py-3 px-4">
                                            <div className="text-white">{b.user_name}</div>
                                            <div className="text-xs text-slate-500">{b.user_email}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div>{b.centre_name}</div>
                                            <div className="text-xs text-slate-500">{b.turf_name} · {b.sport}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div>{ddmmyyyy(b.booking_date)}</div>
                                            <div className="text-xs text-slate-500">{time12h(b.start_time)} – {time12h(b.end_time)}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span data-testid={`source-${b.id}`}
                                                className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border whitespace-nowrap ${
                                                    b.source === "offline"
                                                        ? "bg-[#86efac]/15 text-[#86efac] border-[#86efac]/40"
                                                        : "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40"
                                                }`}>{b.source || "online"}</span>
                                            {b.source === "offline" && b.booked_by_name && (
                                                <div className="mt-1 text-[10px] text-slate-500 uppercase tracking-widest">
                                                    by <span className="text-pink-400 font-bold">{b.booked_by_name?.split(" ")[0]}</span>
                                                    {b.booked_by_role === "manager" && <span className="ml-1 text-pink-400">(mgr)</span>}
                                                </div>
                                            )}
                                            {b.external_source && (
                                                <div className="mt-1">
                                                    <span data-testid={`external-source-badge-${b.id}`}
                                                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-bold uppercase tracking-widest">
                                                        via {b.external_source}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-white font-bold">₹{b.total}</td>
                                        <td className="py-3 px-4 text-right text-[#00FF66] font-bold">₹{b.paid_amount}</td>
                                        <td className={`py-3 px-4 text-right font-bold ${b.balance_due > 0 ? "text-amber-400" : "text-slate-500"}`}>
                                            {b.balance_due > 0 ? `₹${b.balance_due}` : "—"}
                                        </td>
                                        <td className="py-3 px-4">
                                            <PayBadge status={b.payment_status} />
                                            <PaymentLineage b={b} />
                                        </td>
                                        <td className="py-3 px-4">
                                            <StatusBadge status={b.status} />
                                            {b.status === "cancelled" && b.refund_amount != null && (
                                                <div className="mt-1 text-[10px] text-slate-500 uppercase tracking-widest" data-testid={`refund-info-${b.id}`}>
                                                    Refund <span className="text-amber-400 font-bold">{b.cancellation_tier_pct ?? (b.total > 0 ? Math.round((b.refund_amount / b.total) * 100) : 0)}%</span>
                                                    <span> · </span>
                                                    <span className="text-[#00FF66] font-bold">₹{b.refund_amount}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                {/* Iter 35 — Complete / Collect Remaining for "Booked via Other App" */}
                                                {(b.external_source || (b.amount_to_collect || 0) > 0) && b.status !== "cancelled" && (b.payment_status !== "paid") && (() => {
                                                    const rem = Math.max(0, (b.amount_to_collect || 0) - (b.paid_amount || 0));
                                                    const first = (b.collections?.length || 0) === 0;
                                                    return (
                                                        <button onClick={() => setExternalCompleteId(b.id)}
                                                            data-testid={`external-complete-btn-${b.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition whitespace-nowrap">
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            {first ? "Complete" : `Collect Remaining ₹${rem}`}
                                                        </button>
                                                    );
                                                })()}
                                                {b.balance_due > 0 && b.status !== "cancelled" && (
                                                    <button onClick={() => collectBalance(b.id)}
                                                        data-testid={`collect-balance-${b.id}`}
                                                        className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10 transition whitespace-nowrap">
                                                        <CheckCircle2 className="w-3 h-3" /> Collect ₹{b.balance_due}
                                                    </button>
                                                )}
                                                {b.status !== "cancelled" && (
                                                    <>
                                                        <button onClick={() => setModifyTarget(b)}
                                                            data-testid={`modify-${b.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 transition whitespace-nowrap">
                                                            <Pencil className="w-3 h-3" /> Modify
                                                        </button>
                                                        <button onClick={() => cancelByAdmin(b.id)}
                                                            data-testid={`admin-cancel-${b.id}`}
                                                            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded border border-red-900 text-red-400 hover:bg-red-950 transition whitespace-nowrap">
                                                            <XIcon className="w-3 h-3" /> Cancel
                                                        </button>
                                                    </>
                                                )}
                                                {b.balance_due === 0 && b.status === "cancelled" && (
                                                    <span className="text-slate-700 text-xs">—</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Iter 38 — Pagination footer (20/page; resets on filter change) */}
                    {filtered.length > 0 && (
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800 text-xs uppercase tracking-widest text-slate-400" data-testid="bookings-pagination">
                            <div>
                                Showing <span className="text-white font-bold">{(page - 1) * PAGE_SIZE + 1}</span>–<span className="text-white font-bold">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="text-white font-bold">{filtered.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    data-testid="bookings-prev-page"
                                    className="px-3 h-8 rounded border border-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">Prev</button>
                                <span className="text-slate-400">Page <span className="text-white font-bold">{page}</span> / {totalPages}</span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                    data-testid="bookings-next-page"
                                    className="px-3 h-8 rounded border border-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {modifyTarget && (
                <ModifyBookingModal
                    booking={modifyTarget}
                    onClose={() => setModifyTarget(null)}
                    onSaved={() => { setModifyTarget(null); fetchBookings(); }}
                />
            )}
            {/* Iter 35 — External booking completion modal */}
            {externalCompleteId && (() => {
                const b = bookings.find(x => x.id === externalCompleteId);
                if (!b) return null;
                return (
                    <ExternalCompleteModal
                        booking={b}
                        onClose={() => setExternalCompleteId(null)}
                        onSaved={async () => { setExternalCompleteId(null); await fetchBookings(); }}
                    />
                );
            })()}
        </AdminLayout>
    );
}

function PaymentLineage({ b }) {
    // Initial payment: who took the first payment?
    // - If source==="offline" → booked_by_name took the initial paid amount
    // - Else (online) → "Online" via Razorpay (mocked)
    const initialAmount = b.initial_paid_amount ?? b.paid_amount;
    const initialBy = b.source === "offline"
        ? `${b.booked_by_name?.split(" ")[0] || "Staff"}${b.booked_by_role === "manager" ? " (mgr)" : " (admin)"}`
        : "Online";
    // Balance lineage
    const collectedAmount = b.balance_collected_amount;
    const collectedBy = b.balance_collected_by_name?.split(" ")[0];
    const collectedRole = b.balance_collected_by_role === "manager" ? "(mgr)" : "(admin)";
    const collectedMethod = b.balance_collected_method;

    return (
        <div className="mt-1 space-y-0.5 text-[10px] uppercase tracking-widest">
            {initialAmount > 0 && (
                <div className="text-slate-500" data-testid={`paid-initial-${b.id}`}>
                    <span className="text-[#00FF66] font-bold">₹{initialAmount}</span>
                    <span className="text-slate-600"> by </span>
                    <span className={initialBy === "Online" ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>{initialBy}</span>
                </div>
            )}
            {collectedAmount > 0 && (
                <div className="text-slate-500" data-testid={`paid-collected-${b.id}`}>
                    <span className="text-[#00FF66] font-bold">+ ₹{collectedAmount}</span>
                    <span className="text-slate-600"> via </span>
                    <span className="text-cyan-400 font-bold">{collectedMethod}</span>
                    <span className="text-slate-600"> by </span>
                    <span className="text-pink-400 font-bold">{collectedBy} {collectedRole}</span>
                </div>
            )}
        </div>
    );
}

function PayKpi({ label, value, accent, testid }) {
    const accentCls = accent === "green" ? "border-[#00FF66]/40 text-[#00FF66]" :
                      accent === "amber" ? "border-amber-500/40 text-amber-400" :
                      "border-slate-800 text-white";
    return (
        <div className={`bg-slate-900 border ${accentCls} rounded-lg p-4`} data-testid={testid}>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
            <div className={`mt-1 font-display text-2xl font-bold ${accent === "green" ? "text-[#00FF66]" : accent === "amber" ? "text-amber-400" : "text-white"}`}>{value}</div>
        </div>
    );
}

function PayBadge({ status }) {
    const map = {
        paid: { cls: "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40", label: "Paid in Full" },
        partial: { cls: "bg-amber-950 text-amber-400 border-amber-700/50", label: "Partial" },
        pending: { cls: "bg-red-950 text-red-400 border-red-900", label: "Pending" },
    };
    const m = map[status] || map.pending;
    return <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

function StatusBadge({ status }) {
    const map = {
        confirmed: "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40",
        cancelled: "bg-red-950 text-red-400 border-red-900",
        completed: "bg-slate-800 text-slate-400 border-slate-700",
    };
    return <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${map[status] || map.completed}`}>{status}</span>;
}
